<script setup lang="ts">
/**
 * The inventory's lines.
 *
 * The subtotals and the totals are nowhere here, because they are not fields.
 * What is here instead is the list of questions an assurance provider would
 * ask, shown as they arise rather than saved up: a scope 2 reported on one
 * basis only, a category entered twice, a figure nothing can read.
 *
 * Those are questions, not refusals. An inventory in progress is incomplete by
 * definition, and a builder that would not let somebody save one would be
 * useless to the person actually building it.
 *
 * In two tabs: the lines, scope by scope, with those questions over them; and
 * what frames them (unit, periods, intensity, caption), set once.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import {
  GHG_SCOPES,
  ghgProblems,
  SCOPE3_CATEGORIES,
  type GhgLine,
  type GhgScope,
} from '@/domain/ghg'
import { newId, type GhgSection } from '@/domain/section'
import { useRememberedTab } from '@/composables/useRememberedTab'
import InspectorField from '@/sections/InspectorField.vue'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'

const { t } = useI18n()

const props = defineProps<{ section: GhgSection }>()
const emit = defineEmits<{ change: [section: GhgSection] }>()

const problems = computed(() => ghgProblems(props.section.lines))

type GhgTab = 'lines' | 'settings'

const tab = useRememberedTab<GhgTab>('ghg', ['lines', 'settings'])

const tabs = computed<InspectorTab<GhgTab>[]>(() => [
  {
    id: 'lines',
    label: t('inspector.ghg.tabs.lines'),
    icon: TAB_ICONS.lines,
    count: props.section.lines.length,
  },
  { id: 'settings', label: t('inspector.ghg.tabs.settings'), icon: TAB_ICONS.settings },
])

function set(changes: Partial<GhgSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setLine(id: string, changes: Partial<GhgLine>): void {
  set({
    lines: props.section.lines.map((line) => (line.id === id ? { ...line, ...changes } : line)),
  })
}

function add(scope: GhgScope): void {
  set({
    lines: [
      ...props.section.lines,
      {
        id: newId(),
        scope,
        category: scope === 'scope3' ? '1' : '',
        label: '',
        value: '',
        comparative: '',
      },
    ],
  })
}

function remove(id: string): void {
  set({ lines: props.section.lines.filter((line) => line.id !== id) })
}

function linesOf(scope: GhgScope): GhgLine[] {
  return props.section.lines.filter((line) => line.scope === scope)
}

function wording(problem: ReturnType<typeof ghgProblems>[number]): string {
  if (problem.kind === 'unreadable') {
    return t('inspector.ghg.problems.unreadable', { count: problem.count ?? 0 })
  }
  if (problem.kind === 'categoryTwice') {
    return t('inspector.ghg.problems.categoryTwice', {
      category: t(`inspector.ghg.categories.${problem.category ?? '1'}`),
    })
  }
  return t('inspector.ghg.problems.noScope2Basis')
}
</script>

<template>
  <InspectorTabs v-model:open="tab" :tabs="tabs" :label="t('inspector.ghg.tabs.label')">
    <template #lines>
      <div class="fields">
        <ul v-if="problems.length > 0" class="problems">
          <li v-for="(problem, at) in problems" :key="at">{{ wording(problem) }}</li>
        </ul>

        <div v-for="scope in GHG_SCOPES" :key="scope" class="scope">
          <p class="scope__name">{{ t(`inspector.ghg.scopes.${scope}`) }}</p>

          <div v-for="line in linesOf(scope)" :key="line.id" class="line">
            <select
              v-if="scope === 'scope3'"
              class="b-select line__category"
              :value="line.category"
              :aria-label="t('inspector.ghg.category')"
              @change="setLine(line.id, { category: ($event.target as HTMLSelectElement).value })"
            >
              <option v-for="category in SCOPE3_CATEGORIES" :key="category" :value="category">
                {{ category }}. {{ t(`inspector.ghg.categories.${category}`) }}
              </option>
            </select>

            <input
              v-else
              class="b-input line__label"
              :value="line.label"
              :placeholder="t('inspector.ghg.linePlaceholder')"
              @input="setLine(line.id, { label: ($event.target as HTMLInputElement).value })"
            />

            <div class="line__figures">
              <input
                class="b-input"
                :value="line.value"
                :placeholder="section.period || t('inspector.ghg.period')"
                @input="setLine(line.id, { value: ($event.target as HTMLInputElement).value })"
              />
              <input
                class="b-input"
                :value="line.comparative"
                :placeholder="section.comparativePeriod"
                @input="
                  setLine(line.id, { comparative: ($event.target as HTMLInputElement).value })
                "
              />
              <button
                type="button"
                class="b-icon-btn b-btn--danger"
                :aria-label="t('inspector.ghg.removeLine')"
                @click="remove(line.id)"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <button type="button" class="b-btn scope__add" @click="add(scope)">
            {{ t('inspector.ghg.addLine') }}
          </button>
        </div>
      </div>
    </template>

    <template #settings>
      <div class="fields">
        <div class="pair">
          <InspectorField :label="t('inspector.ghg.unit')">
            <input
              class="b-input"
              :value="section.unit"
              placeholder="tCO2e"
              @input="set({ unit: ($event.target as HTMLInputElement).value })"
            />
          </InspectorField>

          <InspectorField :label="t('inspector.ghg.period')">
            <input
              class="b-input"
              :value="section.period"
              placeholder="2026"
              @input="set({ period: ($event.target as HTMLInputElement).value })"
            />
          </InspectorField>
        </div>

        <InspectorField :label="t('inspector.ghg.comparativePeriod')">
          <input
            class="b-input"
            :value="section.comparativePeriod"
            placeholder="2025"
            @input="set({ comparativePeriod: ($event.target as HTMLInputElement).value })"
          />
        </InspectorField>

        <InspectorField
          :label="t('inspector.ghg.intensityLabel')"
          :hint="t('inspector.ghg.intensityHint')"
        >
          <input
            class="b-input"
            :value="section.intensityLabel"
            :placeholder="t('inspector.ghg.intensityPlaceholder')"
            @input="set({ intensityLabel: ($event.target as HTMLInputElement).value })"
          />
        </InspectorField>

        <div class="pair">
          <InspectorField :label="t('inspector.ghg.intensityValue')">
            <input
              class="b-input"
              :value="section.intensityValue"
              @input="set({ intensityValue: ($event.target as HTMLInputElement).value })"
            />
          </InspectorField>

          <InspectorField :label="t('inspector.ghg.intensityComparative')">
            <input
              class="b-input"
              :value="section.intensityComparative"
              @input="set({ intensityComparative: ($event.target as HTMLInputElement).value })"
            />
          </InspectorField>
        </div>

        <InspectorField :label="t('inspector.ghg.caption')">
          <input
            class="b-input"
            :value="section.caption"
            @input="set({ caption: ($event.target as HTMLInputElement).value })"
          />
        </InspectorField>
      </div>
    </template>
  </InspectorTabs>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);
}

/* Questions, not refusals: an inventory in progress is incomplete by
   definition, and these are raised where they can be seen and acted on. */
.problems {
  margin: 0;
  padding: var(--sp-4) var(--sp-5) var(--sp-4) var(--sp-8);
  border-radius: var(--radius);
  background: var(--color-warning-soft);
  color: var(--warn);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.scope {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.scope__name {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.line {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.line__category,
.line__label {
  min-width: 0;
  font-size: var(--text-sm);
}

.line__figures {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: var(--sp-2);
  align-items: center;
}

.line__figures .b-input {
  min-width: 0;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.scope__add {
  align-self: flex-start;
  height: 24px;
  padding: 0 var(--sp-4);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
  font-size: var(--text-sm);
}
</style>
